// Shop-uitstraling voor de publieke retourpagina, één op één naar het
// door JP goedgekeurde referentiebestand (retourpagina-prototype, 20-07):
// header met logo, chat-opening als witte zoekbalk en het subtiele
// Webwinkelkeur-blokje; USP-lijn op één regel; klikbare stepper;
// footer op drie kolommen. Alleen presentatie: de flow en de
// beslisboom staan in RetourPagina.
//
// Webwinkelkeur (bouwronde 17-08): het portaal tekent het keurmerk
// sinds deze ronde zelf — zie WebwinkelKeurWidget.tsx. Het officiële
// sidebar-script, de <valued-widget>-tags, de :defined-CSS en het oude
// fallback-blok zijn hier weg: het script definieerde de custom
// elements op dit domein tóch nooit, dus de fallback wás de weergave.

import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, MessageCircle, PackageOpen } from "lucide-react";

import voeronlineLogo from "@/assets/voeronline-logo.png";
import {
  STANDAARD_RETOURKOSTEN_CENTS,
  VERWERKING_WERKDAGEN,
  formatteerBedrag,
} from "./retourLogica";
import { WebwinkelKeurWidget } from "./WebwinkelKeurWidget";

const SHOP_URL = "https://www.voeronline.nl";
const CONTACT_URL = `${SHOP_URL}/service/`;
// Volledige retourbeleid-uitleg op de shop (beleid-blok, 29-07).
export const RETOURNEREN_URL = `${SHOP_URL}/service/retourneren/`;

// Shopgroen (JP-palet), zelfde waarde als op de retourstatuspagina;
// geen Tailwind-alias in de config, dus als arbitrary value.
const GROEN = "#2E9E5B";

// Eén gedeelde contentkolom voor header, USP-balk, main en footer —
// de 960px-wrap met 20px zijruimte uit de referentie.
export const RETOUR_KOLOM = "mx-auto w-full max-w-[960px] px-5";

// De kaart als subtiel afgezet vlak op de witte pagina: zachte rand,
// licht grijsblauw vlak, geen zwevende schaduw.
export const RETOUR_KAART = "rounded-xl border border-border bg-slate-50 p-6";

// Twee kolommen op desktop (inhoud plus zijkaart van 300 px), gestapeld
// op mobiel. Gedeeld door /retour en /volgen (sessie 137).
export const RETOUR_RASTER =
  "md:grid md:grid-cols-[minmax(0,1fr)_300px] md:items-start md:gap-6";

// ── Chat-koppeling ────────────────────────────────────────────────
// Retourpagina-nova (29-07, JP-besluit 28-07): deze pagina laadt
// dezelfde scriptregel als de shop (/widget/chat.js). Sinds sessie 51
// is dat altijd het Nova-venster (v2); het oude venster is weg.
// Het script zet zelf de vaste chatknop rechtsonder; de zoekbalk in de
// header opent datzelfde venster.

const CHAT_WIDGET_SRC = "/widget/chat.js";

function laadChatWidget() {
  if (document.querySelector(`script[src="${CHAT_WIDGET_SRC}"]`)) return;
  const script = document.createElement("script");
  script.src = CHAT_WIDGET_SRC;
  script.defer = true;
  document.body.appendChild(script);
}

// Geëxporteerd sinds de retourstatuspagina (29-07): het hulpblok daar
// gebruikt exact dezelfde open-mechaniek als de header-zoekbalk.
export function openChatWidget() {
  // Het venster kent geen publieke API; zijn eigen startknop wel. De
  // launcher leeft in de open shadow root van de vaste host
  // (frontend-widget/src/lib/host.ts).
  const novaHost = document.getElementById("vo-chat-v2");
  const novaKnop = novaHost?.shadowRoot?.querySelector<HTMLButtonElement>(
    "button.aui-modal-button",
  );
  if (novaKnop) {
    if (novaKnop.getAttribute("aria-expanded") !== "true") novaKnop.click();
    return;
  }
  // Widget niet geladen (chatbot uitgeschakeld of script geblokkeerd):
  // dan naar het contactoverzicht van de shop.
  window.open(CONTACT_URL, "_blank", "noopener");
}

export function RetourHeader() {
  const { t } = useTranslation();
  useEffect(() => {
    laadChatWidget();
  }, []);
  return (
    <header className="border-b border-border bg-white">
      <div
        className={`${RETOUR_KOLOM} flex h-[62px] items-center justify-between md:h-[72px]`}
      >
        <a
          href={SHOP_URL}
          className="inline-flex shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <img
            src={voeronlineLogo}
            alt={t("retour.shop.naar_webshop")}
            className="h-8 w-auto md:h-10"
            draggable={false}
          />
        </a>
        {/* Mobiel design (opdracht 21-07): tot 760px verdwijnt de
            chat-zoekbalk uit de header — de zwevende Nova-launcher
            rechtsonder blijft de chat-ingang. */}
        <button
          type="button"
          onClick={openChatWidget}
          aria-label={t("retour.shop.chat_label")}
          className="mx-3 hidden h-11 min-w-0 flex-1 items-center gap-2.5 rounded-full border border-input bg-white px-[18px] text-left text-[13.5px] text-muted-foreground hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:flex md:mx-[26px] md:max-w-[430px] md:text-[14.5px]"
        >
          <MessageCircle className="h-[17px] w-[17px] shrink-0" aria-hidden />
          <span className="truncate">{t("retour.shop.chat_zoekbalk")}</span>
        </button>
        {/* Desktop (vanaf 761px): de volle regel — sterren, cijfer en
            hallmark naast elkaar, zoals het voorbeeld op voeronline.nl.
            Geen breedte-cap meer nodig: dit is eigen markup zonder
            shadow-host die naar 0px klapt. */}
        <div className="hidden shrink-0 items-center min-[761px]:flex">
          <WebwinkelKeurWidget variant="usp" />
        </div>
        {/* Mobiel (tot 760px): compact — cijfer en hallmark, want voor
            de sterrenrij is naast het logo geen ruimte. */}
        <div className="flex shrink-0 items-center min-[761px]:hidden">
          <WebwinkelKeurWidget variant="compact" />
        </div>
      </div>
    </header>
  );
}

// Trefwoord vet, de rest normaal — het USP-patroon van de shop. De
// volledige zin staat in i18n; het vette deel is het voorvoegsel.
function UspTekst({ vol, vet }: { vol: string; vet: string }) {
  if (!vol.startsWith(vet)) {
    return <span>{vol}</span>;
  }
  return (
    <span>
      <strong className="font-bold">{vet}</strong>
      {vol.slice(vet.length)}
    </span>
  );
}

export function UspBalk() {
  const { t } = useTranslation();
  const usps = [
    {
      vol: t("retour.shop.usp_bezorgd"),
      vet: t("retour.shop.usp_bezorgd_vet"),
    },
    {
      vol: t("retour.shop.usp_verzending"),
      vet: t("retour.shop.usp_verzending_vet"),
    },
    {
      vol: t("retour.shop.usp_advies"),
      vet: t("retour.shop.usp_advies_vet"),
    },
    {
      vol: t("retour.shop.usp_bedenktijd"),
      vet: t("retour.shop.usp_bedenktijd_vet"),
    },
  ];

  // Mobiel: één automatisch wisselende regel in plaats van vier
  // afgebroken regels. Reduced motion → geen wisseling; screenreaders
  // krijgen altijd de volledige lijst.
  const [actief, setActief] = useState(0);
  useEffect(() => {
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const timer = window.setInterval(
      () => setActief((a) => (a + 1) % usps.length),
      3500,
    );
    return () => window.clearInterval(timer);
  }, [usps.length]);

  return (
    <div className="border-b border-border bg-white">
      <div className={RETOUR_KOLOM}>
        <p
          aria-hidden
          className="flex h-11 items-center justify-center gap-1.5 text-[12.5px] text-brand-navy md:hidden"
        >
          <Check className="h-3 w-3 shrink-0 text-green-600" />
          <UspTekst {...usps[actief]} />
        </p>
        <ul
          aria-label={t("retour.shop.usp_titel")}
          className="sr-only md:not-sr-only md:flex md:h-[38px] md:items-center md:justify-between md:gap-4 md:overflow-hidden"
        >
          {usps.map((usp) => (
            <li
              key={usp.vol}
              className="flex items-center gap-1.5 whitespace-nowrap text-[12.5px] text-muted-foreground"
            >
              <Check
                className="h-3 w-3 shrink-0 text-green-600"
                aria-hidden
              />
              <UspTekst {...usp} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Stepper zoals de referentie: afgeronde stappen zijn klikbaar en
// navigeren terug, de huidige en toekomstige stappen niet. Designronde
// 29-07: elke stap draagt nu een zichtbaar label (Bestelling,
// Artikelen, Reden, Bevestiging) onder de balk. De tekstuele
// "Stap x van y" blijft voor screenreaders behouden.
export function StappenIndicator({
  huidige,
  totaal,
  onNaarStap,
}: {
  huidige: number;
  totaal: number;
  onNaarStap?: (stap: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-2.5">
      <p className="sr-only" aria-live="polite">
        {t("retour.stap_van", { huidige, totaal })}
      </p>
      <div className="flex gap-1.5">
        {Array.from({ length: totaal }, (_, i) => {
          const stap = i + 1;
          const label = t(`retour.stepper_label_${stap}`);
          const klaar = stap < huidige;
          const klikbaar = klaar && onNaarStap !== undefined;
          return (
            <button
              key={stap}
              type="button"
              disabled={!klikbaar}
              onClick={klikbaar ? () => onNaarStap(stap) : undefined}
              aria-current={stap === huidige ? "step" : undefined}
              aria-label={
                klikbaar
                  ? t("retour.stepper_terug_naar", { stap, label })
                  : stap === huidige
                    ? t("retour.stepper_huidige", { stap, label })
                    : t("retour.stepper_stap", { stap, label })
              }
              className={`group min-w-0 flex-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:max-w-[120px] ${
                klikbaar ? "cursor-pointer" : "cursor-default"
              }`}
            >
              <span
                aria-hidden
                className={`block h-[5px] w-full rounded-full transition-colors ${
                  stap <= huidige ? "bg-brand-orange-600" : "bg-muted"
                } ${klikbaar ? "group-hover:bg-brand-orange-700" : ""}`}
              />
              <span
                aria-hidden
                className={`mt-1.5 block truncate text-left text-[11.5px] sm:text-[12.5px] ${
                  stap === huidige
                    ? "font-bold text-gray-900"
                    : klaar
                      ? "text-[#333333]"
                      : "text-gray-500"
                }`}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Lichte balk met groene vinkjes onder de paginakop (designronde
// 29-07), zoals de Lightspeed-retourpagina: geruststellingen op één
// rij. Sinds de herbestel-stylingronde 16-08 neemt de herbestelpagina
// dezelfde balk over met eigen punten, vandaar de losse `punten`.
export function VinkjesStrip({
  punten,
  label,
}: {
  punten: string[];
  label: string;
}) {
  return (
    <ul
      aria-label={label}
      className="mt-5 grid list-none gap-2 rounded-xl border border-border bg-slate-50 px-4 py-3.5 sm:grid-cols-3 sm:gap-3"
    >
      {punten.map((punt) => (
        <li
          key={punt}
          className="flex items-start gap-2 text-[13px] leading-snug text-[#333333]"
        >
          <Check
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0"
            style={{ color: GROEN }}
            strokeWidth={2.5}
          />
          {punt}
        </li>
      ))}
    </ul>
  );
}

// De retour-invulling van die balk: de werkdagen-termijn komt uit de
// gespiegelde serverwaarde, de 14 dagen is de vaste klanttekst
// (opdracht 20-07).
export function ZekerhedenStrip() {
  const { t } = useTranslation();
  return (
    <VinkjesStrip
      label={t("retour.zekerheden.titel")}
      punten={[
        t("retour.zekerheden.bedenktijd"),
        t("retour.zekerheden.verwerking", { dagen: VERWERKING_WERKDAGEN }),
        t("retour.zekerheden.gratis"),
      ]}
    />
  );
}

// Retourbeleid-blok naast het formulier (desktop) of eronder (mobiel):
// kort en scanbaar, met de link naar de volledige uitleg op de shop.
// Het kostenbedrag komt uit de lookup zodra die er is; daarvóór geldt
// de gespiegelde standaardwaarde.
// De kale vorm ervan: kaart met kop en een vinkjeslijst, eventueel met
// iets eronder (zoals de beleid-link). Sinds de herbestel-stylingronde
// 16-08 gebruikt de "Zo werkt het"-zijkaart op /herbestellen dezelfde
// kaart, zodat beide portalen niet uit elkaar lopen.
export function VinkjesKaart({
  titel,
  punten,
  children,
}: {
  titel: string;
  punten: string[];
  children?: ReactNode;
}) {
  return (
    <aside aria-label={titel} className={RETOUR_KAART}>
      <h2 className="text-[16px] font-bold text-gray-900">{titel}</h2>
      <ul className="mt-3 list-none space-y-2.5">
        {punten.map((punt) => (
          <li
            key={punt}
            className="flex items-start gap-2 text-[13.5px] leading-snug text-[#333333]"
          >
            <Check
              aria-hidden
              className="mt-[3px] h-3.5 w-3.5 shrink-0"
              style={{ color: GROEN }}
              strokeWidth={2.5}
            />
            {punt}
          </li>
        ))}
      </ul>
      {children}
    </aside>
  );
}

export function RetourBeleidBlok({
  retourkostenCents,
}: {
  retourkostenCents?: number | null;
}) {
  const { t } = useTranslation();
  const bedrag = formatteerBedrag(
    retourkostenCents ?? STANDAARD_RETOURKOSTEN_CENTS,
  );
  return (
    <VinkjesKaart
      titel={t("retour.beleid.titel")}
      punten={[
        t("retour.beleid.bedenktijd"),
        t("retour.beleid.gratis_fout"),
        t("retour.beleid.kosten", { bedrag }),
        t("retour.beleid.zakken"),
        t("retour.beleid.verwerking", { dagen: VERWERKING_WERKDAGEN }),
      ]}
    >
      <a
        href={RETOURNEREN_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-block rounded-sm text-[13.5px] font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t("retour.beleid.link")}
      </a>
    </VinkjesKaart>
  );
}

// Hulpkaart met de Nova-chat (designronde 29-07): zelfde kaartstijl en
// open-mechaniek als het hulpblok op de retourstatuspagina. Klantzijdig
// altijd Nova, nooit Luna.
export function NovaHulpKaart({ tekst }: { tekst?: string }) {
  const { t } = useTranslation();
  return (
    <section className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 p-5 md:p-6">
      <p className="max-w-[380px] text-[14.5px] text-gray-500">
        {tekst ?? t("retour.nova.hulp_tekst")}
      </p>
      <button
        type="button"
        onClick={openChatWidget}
        className="inline-flex items-center gap-2 rounded-lg border-[1.5px] border-gray-900 bg-white px-[18px] py-2.5 text-[15px] font-bold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <MessageCircle className="h-4 w-4" aria-hidden />
        {t("retour.statuspagina.hulp_chat")}
      </button>
    </section>
  );
}

export function RetourFooter() {
  const { t } = useTranslation();
  const links = [
    { href: CONTACT_URL, label: t("retour.shop.footer_contact") },
    {
      href: RETOURNEREN_URL,
      label: t("retour.shop.footer_retourneren"),
    },
    {
      href: `${SHOP_URL}/service/general-terms-conditions/`,
      label: t("retour.shop.footer_voorwaarden"),
    },
    {
      href: `${SHOP_URL}/service/privacy-policy/`,
      label: t("retour.shop.footer_privacy"),
    },
  ];
  return (
    <footer className="mt-11 border-t border-border bg-slate-50">
      <div
        className={`${RETOUR_KOLOM} grid gap-[22px] pb-10 pt-[34px] text-[14.5px] sm:grid-cols-[1fr_1fr_auto] sm:gap-7`}
      >
        <address className="not-italic leading-[1.9] text-muted-foreground">
          <span className="mb-2.5 block text-[16px] font-bold leading-normal text-brand-navy">
            VoerOnline
          </span>
          Lipsstraat 50 F
          <br />
          5151 RP Drunen
          <br />
          Nederland
        </address>
        <nav aria-label={t("retour.shop.footer_service_titel")}>
          <span className="mb-2.5 block text-[16px] font-bold text-brand-navy">
            {t("retour.shop.footer_service_titel")}
          </span>
          <ul>
            {links.map(({ href, label }) => (
              <li key={href}>
                <a
                  href={href}
                  className="inline-block rounded-sm leading-[1.9] text-muted-foreground underline-offset-2 hover:text-brand-navy hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="min-w-0">
          <span className="mb-2.5 block text-[16px] font-bold text-brand-navy">
            {t("retour.shop.beoordeeld_titel")}
          </span>
          {/* Bouwronde 17-08, door JP goedgekeurde vorm: sterren en
              cijfer op één regel, het hallmark eronder en daaronder de
              stille regel met score en aantal reviews. Eigen markup, dus
              flush links onder de kop zonder de breedte-cap en de
              transform-compensatie die de officiële widget nodig had. */}
          <WebwinkelKeurWidget variant="footer" />
        </div>
      </div>
    </footer>
  );
}

// Productfoto met nette terugval: geen afbeelding uit Lightspeed of een
// kapotte URL → neutrale placeholder in plaats van een gebroken plaatje.
export function ProductFoto({ url }: { url: string | null }) {
  const [mislukt, setMislukt] = useState(false);
  if (!url || mislukt) {
    return (
      <span
        aria-hidden
        className="grid h-14 w-14 shrink-0 place-items-center rounded-[10px] bg-slate-100"
      >
        <PackageOpen className="h-6 w-6 text-muted-foreground" />
      </span>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className="h-14 w-14 shrink-0 rounded-[10px] bg-slate-100 object-contain"
      onError={() => setMislukt(true)}
    />
  );
}
